import { useTranslation } from 'react-i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useI18n } from 'hooks/usei18n';
import useWindowWidth from 'hooks/useWindowWidth';
import { getLegalDocument, type LegalDocumentKind } from 'utils/legalDocuments';
import './LegalDocument.scss';

interface ILegalDocumentProps {
  kind: LegalDocumentKind
}

/** A real, indexable page for a document that is published as a PDF: heading, links, inline viewer. */
const LegalDocument = ({ kind }: ILegalDocumentProps) => {
  const { t } = useTranslation();
  const { language } = useI18n();
  const { isMobile } = useWindowWidth();
  const { url, fileName } = getLegalDocument(kind, language);
  const title = t(`footer.${kind}`);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{`${title} | 2kiddo`}</title>
          <meta name="description" content={t('legalDocument.lead')}/>
        </Helmet>
      </HelmetProvider>
      <section className="flex flex-column legal-document">
        <h1 className="legal-document-title">{title}</h1>
        <p className="legal-document-lead">{t('legalDocument.lead')}</p>
        <div className="flex legal-document-actions">
          <a className="legal-document-link" href={url} target="_blank" rel="noreferrer">
            {t('legalDocument.open')}
          </a>
          <a className="legal-document-link" href={url} download={fileName}>
            {t('legalDocument.download')}
          </a>
        </div>
        {!isMobile && (
          <object className="legal-document-viewer" data={url} type="application/pdf" aria-label={title}>
            <p>{t('legalDocument.fallback')}</p>
          </object>
        )}
      </section>
    </>
  );
};

export default LegalDocument;
